import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import './KPICard.scss';
export const KPICardSkeleton = () => (
  <div className="kpi-card kpi-card--skeleton">
    <div className="kpi-header">
      <div className="kpi-info">
        <div className="skeleton skeleton--title" />
        <div className="skeleton skeleton--value" />
      </div>
      <div className="skeleton skeleton--icon" />
    </div>
    <div className="skeleton skeleton--footer" />
  </div>
);

const KPICard = ({ 
  title, 
  value, 
  icon: Icon, 
  trend, 
  trendLabel, 
  color = 'blue',
  to,
  linkLabel,
  infoTitle,
  infoItems = [],
  infoButtonLabel = 'Detaje'
}) => {
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const headerRef = useRef(null);
  const iconWrapRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const isPositive = trend && trend > 0;
  const hasTrend = trend !== undefined && trend !== null;
  const normalizedInfoItems = useMemo(
    () => (Array.isArray(infoItems) ? infoItems.filter((item) => item && item.label) : []),
    [infoItems]
  );
  const hasInfo = normalizedInfoItems.length > 0;

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      const headerHovered = !!headerRef.current?.matches(':hover');
      const iconAreaHovered = !!iconWrapRef.current?.matches(':hover');

      if (!headerHovered && !iconAreaHovered) {
        setIsInfoOpen(false);
      }
    }, 180);
  };

  const handleHeaderMouseEnter = () => {
    if (hasInfo) {
      clearCloseTimeout();
      setIsInfoOpen(true);
    }
  };

  const handleHeaderMouseLeave = () => {
    if (!hasInfo) {
      return;
    }

    scheduleClose();
  };

  const handleIconWrapMouseEnter = () => {
    clearCloseTimeout();
  };

  const handleIconWrapMouseLeave = () => {
    if (!hasInfo) {
      return;
    }

    scheduleClose();
  };

  useEffect(() => {
    if (!isInfoOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (iconWrapRef.current && !iconWrapRef.current.contains(event.target)) {
        setIsInfoOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isInfoOpen]);

  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, []);

  return (
    <div className={`kpi-card kpi-card--${color} ${isInfoOpen ? 'kpi-card--dropdown-open' : ''}`}>
      <div
        className="kpi-header"
        ref={headerRef}
        onMouseEnter={hasInfo ? handleHeaderMouseEnter : undefined}
        onMouseLeave={hasInfo ? handleHeaderMouseLeave : undefined}
      >
        <div className="kpi-info">
          <p className="kpi-title">{title}</p>
          <h3 className="kpi-value">{value}</h3>
        </div>
        <div
          className="kpi-icon-wrap"
          ref={iconWrapRef}
          onMouseEnter={hasInfo ? handleIconWrapMouseEnter : undefined}
          onMouseLeave={hasInfo ? handleIconWrapMouseLeave : undefined}
        >
          <div
            className={`kpi-icon ${color} ${hasInfo ? 'kpi-icon--interactive' : ''}`}
            onClick={hasInfo ? () => setIsInfoOpen((prev) => !prev) : undefined}
            role={hasInfo ? 'button' : undefined}
            tabIndex={hasInfo ? 0 : undefined}
            onKeyDown={
              hasInfo
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setIsInfoOpen((prev) => !prev);
                    }
                  }
                : undefined
            }
            aria-label={hasInfo ? (isInfoOpen ? 'Mbyll detaje' : infoButtonLabel) : undefined}
            title={hasInfo ? (isInfoOpen ? 'Mbyll detaje' : 'Shiko detaje') : undefined}
          >
            <Icon />
          </div>

          {hasInfo && isInfoOpen && (
            <div className="kpi-breakdown kpi-breakdown--from-icon">
              <div className="kpi-breakdown__panel">
                {infoTitle ? <p className="kpi-breakdown__title">{infoTitle}</p> : null}
                <div className="kpi-breakdown__list">
                  {normalizedInfoItems.map((item) => (
                    <div className="kpi-breakdown__row" key={`${item.label}-${item.value}`}>
                      <span className="kpi-breakdown__label">{item.label}</span>
                      <span className="kpi-breakdown__value">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {hasTrend && (
        <div className="kpi-trend">
          <span className={`trend-value ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? <TrendingUp /> : <TrendingDown />}
            {Math.abs(trend)}%
          </span>
          <span className="trend-label">kundrejt {trendLabel}</span>
        </div>
      )}

      {to && linkLabel && (
        <Link to={to} className="kpi-link">
          <span>{linkLabel}</span>
          <ArrowRight />
        </Link>
      )}
    </div>
  );
};

export default KPICard;